// Turning a drawn stroke into a flyable path: runway-strip / pad snapping,
// the roll-in guide (assignPath), and the live un-snapped preview while a
// finger is still down (previewPath). No tick-loop concerns and no mutation
// of anything but the one `Aircraft` a call is given — this file never
// touches `world.planes` as a whole or advances time.
import type { Aircraft, Command, Runway, Vec } from './types.ts';
import type { World } from './world.ts';
import { clamp, dist, resample, smooth } from './geometry.ts';
import { ROLLIN_FRAC, SNAP_PAD_EXTRA, SNAP_RUNWAY, SNAP_TAIL } from './tuning.ts';

/** how much nearer the next waypoint must be to count as still-receding wake */
const CLOSER_EPS = 0.5;

/**
 * Drop the leading waypoints an aircraft has already flown past.
 *
 * A stroke is drawn *from* the aircraft, and the aircraft keeps flying while
 * it is being drawn — by the time the finger lifts, a second of drawing has
 * put it 30-50 px along its own route. The v10 prototype handled this by
 * aliasing: `p.path = drag.pts` shared one array with the input layer, so the
 * waypoints `step()` consumed with `path.shift()` were gone from the stroke
 * for good, and neither the live preview nor the commit could put them back.
 * The port copies instead of aliasing (the sim owns its own arrays), so every
 * pointermove and the final commit handed the aircraft its own wake again: it
 * turned around, flew back to where the finger had started, and looped.
 *
 * Same outcome, without the shared array: walk the head forward while it is
 * still getting *closer* to the aircraft and cut there. That stops at the
 * first local minimum — the aircraft's own position along the stroke — so a
 * route the player deliberately drew backwards or around the aircraft (whose
 * points only get farther away from the head) is never trimmed, and neither
 * is a stroke that loops back near the aircraft much later.
 */
function dropFlownPrefix(p: Aircraft, pts: Vec[]): Vec[] {
  let i = 0;
  // `CLOSER_EPS`, not a bare `<`: points that are all much the same distance
  // away (a stroke drawn as an arc around the aircraft) must not creep the
  // cursor forward on floating-point noise. Wake recedes by a whole waypoint
  // spacing per point, so it clears this by a wide margin.
  while (i < pts.length - 1 && dist(pts[i + 1], p) < dist(pts[i], p) - CLOSER_EPS) i++;
  return i > 0 ? pts.slice(i) : pts;
}

/** the point `s` px along a strip, measured from its threshold */
export function pointAlongRunway(r: Runway, s: number): Vec {
  const t = s / r.len;
  return { x: r.x1 + (r.x2 - r.x1) * t, y: r.y1 + (r.y2 - r.y1) * t };
}

/** distance from a point to a strip: its centreline segment, not just the threshold */
export function distToStrip(r: Runway, x: number, y: number): number {
  const dx = r.x2 - r.x1, dy = r.y2 - r.y1;
  const t = clamp(((x - r.x1) * dx + (y - r.y1) * dy) / (r.len * r.len), 0, 1);
  return Math.hypot(x - (r.x1 + dx * t), y - (r.y1 + dy * t));
}

/** how far along a strip a point lies, in px from the threshold (negative = still on the approach) */
function alongStrip(r: Runway, pt: Vec): number {
  return ((pt.x - r.x1) * (r.x2 - r.x1) + (pt.y - r.y1) * (r.y2 - r.y1)) / r.len;
}

/** the strip a drawn point is on — nearest centreline within `SNAP_RUNWAY`, or null */
function stripUnder(w: World, pt: Vec): Runway | null {
  let best: Runway | null = null, bd = SNAP_RUNWAY;
  for (const r of w.runways) {
    const d = distToStrip(r, pt.x, pt.y);
    if (d < bd) { bd = d; best = r; }
  }
  return best;
}

/**
 * Take a stroke that stopped short of the runway the rest of the way onto it,
 * in place; returns how many waypoints that took (flown, never drawn — see
 * `Aircraft.pathSnapped`). A stroke already drawn down the strip needs none of
 * them, and gets none: how far along the strip an aircraft touches down is
 * decided by how far the player drew, never by the game.
 */
function pushLandingTail(r: Runway, pts: Vec[]): number {
  const s = alongStrip(r, pts[pts.length - 1]);
  const before = pts.length;
  if (s < 0) pts.push({ x: r.x1, y: r.y1 });              // over the bars first
  const guide = ROLLIN_FRAC * r.len;
  if (s < guide) pts.push(pointAlongRunway(r, guide));    // ...along the runway, not at a point on it
  return pts.length - before;
}

/**
 * Assign a drawn path (logical px). Mirrors the prototype's pointerup rule:
 * the last several points are checked against the runways / pads so a finger
 * overshoot still counts. Returns what the path was snapped to.
 */
export function assignPath(w: World, p: Aircraft, raw: Vec[], opts: { smooth?: boolean } = {}): 'runway' | 'pad' | 'free' {
  if (w.log) {
    // the raw stroke exactly as given, before smoothing/snapping — replay
    // must go through the same code path assignPath itself is about to run
    const cmd: Command = {
      tick: w.tick, t: 'path', plane: p.id,
      pts: raw.map(v => ({ x: v.x, y: v.y })),
      ...(opts.smooth !== undefined ? { smooth: opts.smooth } : {}),
    };
    w.log.push(cmd);
  }
  let pts = raw.map(v => ({ x: v.x, y: v.y }));
  if (opts.smooth !== false && pts.length > 3) pts = resample(smooth(pts, 1), 6);
  pts = dropFlownPrefix(p, pts);
  p.holding = false; p.landing = false; p.runway = null; p.pad = null; p.pathSnapped = 0;
  p.idleSince = 0;
  w.stats.commands++;
  let result: 'runway' | 'pad' | 'free' = 'free';
  outer:
  for (let i = pts.length - 1; i >= Math.max(0, pts.length - SNAP_TAIL); i--) {
    if (p.type.kind === 'heli') {
      for (const pd of w.pads) {
        if (Math.hypot(pts[i].x - pd.x, pts[i].y - pd.y) < pd.r + SNAP_PAD_EXTRA) {
          pts.length = i + 1;
          pts.push({ x: pd.x, y: pd.y });
          p.pathSnapped = 1;
          p.landing = true; p.pad = pd; result = 'pad';
          break outer;
        }
      }
    } else {
      // the whole strip is a landing zone, not just the bars: a line drawn
      // down the runway is how you ask for a longer, later touchdown
      const r = stripUnder(w, pts[i]);
      if (r) {
        pts.length = i + 1;                                     // trim the overshoot
        p.pathSnapped = pushLandingTail(r, pts);
        p.landing = true; p.runway = r; result = 'runway';
        break outer;
      }
    }
  }
  p.path = pts;
  return result;
}

/**
 * Live preview while dragging: replaces the path without snapping (the
 * prototype's pointermove behaviour), minus whatever the aircraft has
 * already flown past — see `dropFlownPrefix`. Deliberately not a recorded
 * `Command` (src/sim/replay.ts): it fires on every pointermove — dozens of
 * calls per stroke — and only `src/input/pointer.ts` (real touch/pen input)
 * ever calls it; bots go straight to `assignPath` once a route is decided,
 * so nothing the lab or a bot-driven replay exercises depends on it. Note
 * for anyone wiring up human-session replay later: its writes (`path`,
 * `landing`, `runway`, `pad`) *are* read by `step()`, so a human game can in
 * principle land, go around or collide while a stroke is still being drawn,
 * before `assignPath` ever commits — a replay of a real phone session is
 * only exact if no such mid-drag outcome occurred, or the log is extended
 * to cover this later.
 */
export function previewPath(p: Aircraft, raw: Vec[]): void {
  p.path = dropFlownPrefix(p, raw.map(v => ({ x: v.x, y: v.y })));
  p.pathSnapped = 0;
  p.landing = false; p.runway = null; p.pad = null; p.holding = false;
}
